/**
 * EditAgentModal
 * Modal for editing a generated agent's name, description, system instructions,
 * trigger keywords, reference files, and tools.
 */

import React, { useState } from 'react';
import { Bot, Plus, X } from 'lucide-react';
import { Modal, Button, Input, Textarea } from '../../ui';
import type { GeneratedAgent } from '../../../types';
import styles from './EditAgentModal.module.css';

export interface EditAgentModalProps {
  agent: GeneratedAgent;
  onSave: (updatedAgent: GeneratedAgent) => void;
  onClose: () => void;
}

export const EditAgentModal: React.FC<EditAgentModalProps> = ({
  agent,
  onSave,
  onClose,
}) => {
  const [form, setForm] = useState<GeneratedAgent>({ ...agent });
  const [isSaving, setIsSaving] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newTool, setNewTool] = useState('');
  const [newRefFile, setNewRefFile] = useState('');

  /** Handle text field changes */
  const handleChange = (field: keyof GeneratedAgent, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /** Add a keyword tag */
  const addKeyword = () => {
    const trimmed = newKeyword.trim();
    if (trimmed && !form.triggerKeywords.includes(trimmed)) {
      setForm((prev) => ({
        ...prev,
        triggerKeywords: [...prev.triggerKeywords, trimmed],
      }));
      setNewKeyword('');
    }
  };

  /** Remove a keyword tag */
  const removeKeyword = (kw: string) => {
    setForm((prev) => ({
      ...prev,
      triggerKeywords: prev.triggerKeywords.filter((k) => k !== kw),
    }));
  };

  /** Add a tool */
  const addTool = () => {
    const trimmed = newTool.trim();
    if (trimmed && !form.tools.includes(trimmed)) {
      setForm((prev) => ({
        ...prev,
        tools: [...prev.tools, trimmed],
      }));
      setNewTool('');
    }
  };

  /** Remove a tool */
  const removeTool = (tool: string) => {
    setForm((prev) => ({
      ...prev,
      tools: prev.tools.filter((t) => t !== tool),
    }));
  };

  /** Add a reference file */
  const addRefFile = () => {
    const trimmed = newRefFile.trim();
    if (trimmed && !form.referenceFiles.includes(trimmed)) {
      setForm((prev) => ({
        ...prev,
        referenceFiles: [...prev.referenceFiles, trimmed],
      }));
      setNewRefFile('');
    }
  };

  /** Remove a reference file */
  const removeRefFile = (file: string) => {
    setForm((prev) => ({
      ...prev,
      referenceFiles: prev.referenceFiles.filter((f) => f !== file),
    }));
  };

  /** Handle save */
  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await onSave(form);
    } finally {
      setIsSaving(false);
    }
  };

  /** Handle key press for adding tags */
  const handleKeyPress = (
    e: React.KeyboardEvent,
    addFn: () => void
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addFn();
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Agent"
      size="lg"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!form.name.trim()}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        {/* Agent Icon + Name */}
        <div className={styles.nameRow}>
          <span className={styles.agentIcon}>{form.icon || '🤖'}</span>
          <div className={styles.nameField}>
            <label className={styles.label}>Agent Name</label>
            <Input
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Agent name"
            />
          </div>
        </div>

        {/* Description */}
        <div className={styles.field}>
          <label className={styles.label}>Description</label>
          <Textarea
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Describe what this agent does..."
            rows={2}
          />
        </div>

        {/* System Instructions */}
        <div className={styles.field}>
          <label className={styles.label}>System Instructions</label>
          <Textarea
            value={form.systemInstructions}
            onChange={(e) => handleChange('systemInstructions', e.target.value)}
            placeholder="System instructions for the agent..."
            rows={8}
            className={styles.codeTextarea}
          />
        </div>

        {/* Trigger Keywords */}
        <div className={styles.field}>
          <label className={styles.label}>Trigger Keywords</label>
          <div className={styles.tagInput}>
            <Input
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => handleKeyPress(e, addKeyword)}
              placeholder="Add keyword and press Enter"
            />
            <Button variant="secondary" size="sm" onClick={addKeyword} disabled={!newKeyword.trim()}>
              <Plus size={14} />
            </Button>
          </div>
          <div className={styles.tags}>
            {(form.triggerKeywords || []).map((kw) => (
              <span key={kw} className={styles.tag}>
                {kw}
                <button className={styles.tagRemove} onClick={() => removeKeyword(kw)}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Reference Files */}
        <div className={styles.field}>
          <label className={styles.label}>Reference Files</label>
          <div className={styles.tagInput}>
            <Input
              value={newRefFile}
              onChange={(e) => setNewRefFile(e.target.value)}
              onKeyDown={(e) => handleKeyPress(e, addRefFile)}
              placeholder="Add file path and press Enter"
            />
            <Button variant="secondary" size="sm" onClick={addRefFile} disabled={!newRefFile.trim()}>
              <Plus size={14} />
            </Button>
          </div>
          <div className={styles.tags}>
            {(form.referenceFiles || []).map((file) => (
              <span key={file} className={`${styles.tag} ${styles.fileTag}`}>
                {file}
                <button className={styles.tagRemove} onClick={() => removeRefFile(file)}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Tools */}
        <div className={styles.field}>
          <label className={styles.label}>Tools</label>
          <div className={styles.tagInput}>
            <Input
              value={newTool}
              onChange={(e) => setNewTool(e.target.value)}
              onKeyDown={(e) => handleKeyPress(e, addTool)}
              placeholder="Add tool and press Enter"
            />
            <Button variant="secondary" size="sm" onClick={addTool} disabled={!newTool.trim()}>
              <Plus size={14} />
            </Button>
          </div>
          <div className={styles.tags}>
            {(form.tools || []).map((tool) => (
              <span key={tool} className={`${styles.tag} ${styles.toolTag}`}>
                {tool}
                <button className={styles.tagRemove} onClick={() => removeTool(tool)}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditAgentModal;
